import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import ClearIcon from '@material-ui/icons/Clear';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import { TextField, Button } from '@ccm-office/ccm-ui';
import { ILoginUser } from './interfaces/dtos/ILoginUser';
import { ILoginUserErrors } from './interfaces/dtos/ILoginUserErrors';
import { IShowPassword } from './interfaces/dtos/IShowPassword';
import { newStyles } from './login-styles';
import brandlogoname from './media/ncr-logo-new.svg';
import { Alert } from '@material-ui/lab';

const LoginForm: React.FC<ILoginFormProps> = (props: ILoginFormProps): JSX.Element => {
  const errorRef = useRef<any>(null);
  const classes = newStyles({ errorHeight: errorRef });
  const { t } = useTranslation();
  const { user } = props;
  const [showPassword, setShowPassword] = useState<IShowPassword>({ displayPassword: false });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.currentTarget;

    if (name === 'organization') {
      props.onChange(name, value?.toLowerCase());
    } else {
      props.onChange(name, value);
    }
  };

  const handleCancel = (fieldName: string) => {
    props.onChange(fieldName, '');
  };

  const handleClickShowPassword = useCallback((): void => {
    setShowPassword({
      ...showPassword,
      displayPassword: !showPassword.displayPassword,
    });
  }, [showPassword]);

  return (
    <Grid container item sm={12} justify="center">
      <Grid container item sm={3} direction="column" justify="center" alignItems="center">
        <Grid container justify="center">
          <div className={classes.iconShoppingContainer}>
            <ShoppingCartIcon className={classes.iconShopping} />
          </div>
          <Typography className={classes.loginTitleEm} data-testid="login-title-em" variant="h2">
            Emerald&nbsp;
          </Typography>
          <Typography className={classes.loginTitleCCM} data-testid="login-title-ccm" variant="h2">
            CCM
          </Typography>
        </Grid>
        <div className={classes.margintopuserdiv}>
          <form
            className={classes.form}
            onSubmit={props.onLogin}
            noValidate
            data-testid="FormSubmit"
          >
            {props?.errors?.description && (
              <div ref={errorRef}>
                <Alert severity={'error'} variant={'outlined'}>
                  {props.errors.description}
                </Alert>
              </div>
            )}
            <Grid container spacing={2}>
              <Grid item sm={12}>
                <Typography data-testid="form-title" variant="h1">
                  {t('label.signIn')}
                </Typography>
              </Grid>
              <Grid item sm={12}>
                <TextField
                  variant="outlined"
                  margin="normal"
                  data-testid="UserName"
                  fullWidth
                  inputProps={{ 'data-testid': 'username-input' }}
                  id="userName"
                  label={t('label.username')}
                  name="userName"
                  placeholder={t('placeholder.enterUsername')}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleCancel('userName')}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  value={user.userName}
                  autoComplete="userName"
                  helperText={props.errors.userName}
                  FormHelperTextProps={{
                    className: classes.helperText,
                  }}
                  autoFocus
                />
              </Grid>
              <Grid item sm={12}>
                <TextField
                  id="password"
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  inputProps={{ 'data-testid': 'password-input' }}
                  data-testid="Password"
                  name="password"
                  label={t('label.peakFord')}
                  placeholder={t('placeholder.peakFord')}
                  type={showPassword.displayPassword ? 'text' : 'password'}
                  onChange={handleChange}
                  value={user.password}
                  helperText={props.errors.password}
                  FormHelperTextProps={{
                    className: classes.helperText,
                  }}
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label="Toggle password visibility"
                          onClick={handleClickShowPassword}
                          tabIndex={-1}
                          data-testid="toggle-password-btn"
                        >
                          {showPassword.displayPassword ? (
                            <Visibility fontSize="small" />
                          ) : (
                            <VisibilityOff fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item sm={12}>
                <TextField
                  variant="outlined"
                  margin="normal"
                  data-testid="organization"
                  fullWidth
                  inputProps={{ 'data-testid': 'organization-input' }}
                  id="organization"
                  label={t('title.organization')}
                  name="organization"
                  placeholder={t('placeholder.organization')}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleCancel('organization')}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  value={user.organization}
                  autoComplete="organization"
                  helperText={props.errors.organization}
                  FormHelperTextProps={{
                    className: classes.helperText,
                  }}
                />
              </Grid>

              <Grid item sm={6}>
                <Button fullWidth data-testid="Submit" type="submit" size="large">
                  {t('button.submit')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </div>
        <Grid container justify="center">
          <Typography data-testid="project-version" variant="subtitle1">
            {t('label.poweredBy')}&nbsp;
          </Typography>
          <img src={brandlogoname} alt="Logo" />
        </Grid>
      </Grid>
    </Grid>
  );
};

interface ILoginFormProps {
  setLogin: (state: ILoginUser) => void;
  user: ILoginUser;
  errors: ILoginUserErrors;
  onLogin: (event: React.FormEvent<HTMLFormElement>) => void;
  onChange: (name: string, value: string) => void;
}

export default LoginForm;
